import React from "react";
import clsx from "clsx";
import { Destination } from "../../../../../../../models/Entities";
import {
  MergeLinkDescriptionTable,
  MergeLinkFooter,
  MergeLinkNavButtons,
} from "../../../../../../common/MergeLinkDialog/components";
import { Alert, Text } from "@merge-api/merge-javascript-shared";
import { Lock } from "lucide-react";
import CreateDestinationForm from "./components/CreateDestinationForm";
import useCreateSyncConnection from "./hooks/useCreateSyncConnection";
import { useState } from "react";

type ConnectDestinationStepProps = {
  className?: string;
  destination: Destination;
  onBack: () => void;
  onClose: () => void;
  onNext: (error?: { message: string; third_party_error: boolean }) => void;
  authFields: {
    [key: string]: string;
  };
  setAuthFields: React.Dispatch<
    React.SetStateAction<{
      [key: string]: string;
    }>
  >;
};

const ConnectDestinationStep = ({
  className,
  onClose,
  onBack,
  destination,
  onNext,
  authFields,
  setAuthFields,
}: ConnectDestinationStepProps) => {
  // hooks
  const [symmetricDataKey, setSymmetricDataKey] = useState<string | undefined>();
  const { onSubmit, isSubmitLoading, isSubmitDisabled } = useCreateSyncConnection({
    onNext,
    authFields,
    destination,
    symmetricDataKey,
  });

  // event handlers
  const onAuthFieldChange = (key: string, value: string) => {
    setAuthFields({
      ...authFields,
      [key]: value,
    });
  };

  return (
    <div className={clsx("h-full flex flex-col justify-start pt-5", className)}>
      {/* Header */}
      <MergeLinkNavButtons onClose={onClose} onBack={onBack} />
      <div className="flex flex-row justify-between items-center mx-5 mb-5 mt-3">
        <Text variant="h3">Connect Destination</Text>
        <img className="flex h-[40px]" src={destination.image} />
      </div>

      {/* Body */}
      <div className="flex flex-row overflow-hidden border-t border-b border-gray-10 h-full">
        {/* Form */}
        <div className="flex w-full p-5 overflow-y-auto">
          <CreateDestinationForm
            authSchema={destination!.auth_schema}
            authFields={authFields}
            onAuthFieldsChange={onAuthFieldChange}
            symmetricDataKey={symmetricDataKey}
            setSymmetricDataKey={setSymmetricDataKey}
          />
        </div>

        {/* Description */}
        <div className="flex m-5 w-full">
          <MergeLinkDescriptionTable
            title="How to find your credentials"
            description={destination!.description}
            helpGuideUrl={destination!.help_guide_url}
          />
        </div>
      </div>

      {/* Footer */}
      <MergeLinkFooter
        submitButtonText="Connect"
        onSubmit={onSubmit}
        isSubmitLoading={isSubmitLoading}
        isSubmitDisabled={isSubmitDisabled}
      >
        <Alert size="sm" color="gray" icon={<Lock size={12} />}>
          Your data is protected with 256-bit SSL bank-level encryption
        </Alert>
      </MergeLinkFooter>
    </div>
  );
};

export default ConnectDestinationStep;
